import React from 'react';
import { Music, Send, Link2, Mail, MessageSquare } from 'lucide-react';

/**
 * "What's new": song choices go out once per project. EDITORS (operator ruling, 2026-09-15).
 *
 * Single screen. Every current editor already knows the song panel; what changed is the
 * rule around it, so this states the rule and what it asks of them, not a tutorial.
 * The panel, the one-time song intro and the Editor Guide say the same thing.
 */

const Eyebrow = ({ children }) => (
  <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block">
    {children}
  </span>
);

export function SongOnceEditorAnnouncement({ onAcknowledge }) {
  return (
    <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid="whats-new-song-once-editor">
      <div className="p-6 pb-5 border-b border-ug-border-subtle">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-ug-gold/15 text-ug-gold border border-ug-gold/25">New</span>
          <Music className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl text-zinc-100 mb-2">Song choices now go out once</h2>
        <p className="text-[13px] text-zinc-100/75 leading-relaxed">
          Going back and forth on songs was costing days on a project, and every one of those days pushed back the
          rest of your queue. So when a client asks to okay the songs, you now send one set of song choices, and the
          client works from it.
        </p>
        {/* Operator, 2026-09-15: who this applies to comes first, in red. Without it the notice read as
            "send song choices on every project", which is not the rule. */}
        <div className="mt-3 rounded-lg border border-ug-status-red/40 bg-ug-status-red/[0.06] px-3 py-2.5" data-testid="song-once-editor-announcement-audience">
          {/* text-red-300, not text-ug-status-red: the token measured 4.50:1 on this tinted box in dark.
              index.css maps red-300 to red-700 in light. */}
          <p className="text-[12px] font-semibold text-red-300 leading-relaxed">
            You only send song choices when a client asks to okay the songs before you edit. When the client provides their own music, nothing changes.
          </p>
        </div>
      </div>
      <div className="p-6 py-5 bg-ug-surface/30 space-y-3">
        <Eyebrow>What changes for you</Eyebrow>
        {/* flex + gap, not space-y: the rows must never touch, with or without a wrapper element. */}
        <div className="flex flex-col gap-2">
          {[[Send, 'Send one set: a section per part of the video, or one section with several options to choose from'],
            [Link2, 'The client keeps any of your options and can add links to their own song(s), within 24 hours'],
            [Mail, 'You and your project manager both get an email with their final songs']].map(([Icon, text], i) => (
            <div key={i} className="flex items-center gap-2 rounded bg-ug-surface border border-ug-border-strong px-2.5 py-2">
              <Icon className="w-3.5 h-3.5 text-oxidized-teal shrink-0" />
              <span className="text-[11px] text-zinc-100/75">{text}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="p-6 py-5 space-y-2.5 border-t border-ug-border-subtle">
        {[
          ['There is no second set', 'Once you send, the button is gone. Send your best options and check every link opens first.'],
          ['Their answer is final', 'Use the songs they send as sent. A song the client supplies is theirs to license.'],
          ['Anything more about songs goes to chat', 'Ask your project manager. You never message the client directly.'],
        ].map(([t, d], i) => (
          <div key={i} className="flex gap-3">
            <span className="shrink-0 w-5 h-5 rounded-full bg-ug-gold/15 border border-ug-gold/25 text-ug-gold font-sans text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
            <p className="text-[12px] text-zinc-100/75 leading-relaxed">
              <span className="text-zinc-100 font-medium">{t}.</span> {d}
            </p>
          </div>
        ))}
        <div className="flex gap-3 pt-1">
          <MessageSquare className="shrink-0 w-4 h-4 text-slate-500 mt-0.5" strokeWidth={2} />
          <p className="text-[11px] text-slate-500 leading-relaxed">
            The full rules are in the Editor Guide, under Getting Started, Song Choices.
          </p>
        </div>
      </div>
      <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20">
        <button data-testid="whats-new-next" onClick={() => onAcknowledge && onAcknowledge()}
                className="tap-auto w-full py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider">
          Got it
        </button>
      </div>
    </div>
  );
}

export default SongOnceEditorAnnouncement;
